import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../api";
import { Camera, ShieldCheck, AlertTriangle, Maximize2, Hourglass } from "lucide-react";

/**
 * ProctorGate — pre-assessment consent + ID verification.
 * Steps:
 *  1. Consent (DPDP) + camera/mic permission grant
 *  2. ID + Selfie capture → Gemini verification
 *  3. On failed verify: participant can "Request manual review by Mercury".
 *     Component polls until admin grants override, then proceeds.
 *  4. Fullscreen lock + call onReady()
 */

/** Portrait-orientation SVG mockup of a VI Employee ID card — modelled on
 * the real cards VI shared (dominant red body, small "VI!" wordmark top-left,
 * white swoosh curve, greyscale silhouette portrait, yellow "hi" flourish,
 * "I'm ____" caption). Shown in the ID slot before capture so first-timers
 * recognise the exact card to photograph. Uses a generic silhouette + blank
 * name so it can never be mistaken for a real submission. */
function ViIdSample() {
  return (
    <svg
      viewBox="0 0 200 300"
      className="w-[120px] h-auto drop-shadow-md"
      aria-label="Sample VI Employee ID card"
      data-testid="vi-id-sample-svg"
    >
      {/* card body */}
      <rect x="0" y="0" width="200" height="300" rx="10" fill="#EE2737" />

      {/* white swoosh curve (top-right dominant sweep) */}
      <path
        d="M 200 0 Q 210 40 200 90 Q 180 130 90 150 Q 30 165 60 220 Q 90 260 200 250 L 200 0 Z"
        fill="#FFFFFF"
      />
      {/* right-edge red tab peeking through the swoosh */}
      <path d="M 200 90 Q 160 110 165 155 Q 178 175 200 170 Z" fill="#EE2737" />

      {/* VI wordmark top-left */}
      <text
        x="14" y="42"
        fontFamily="Inter, sans-serif"
        fontSize="34"
        fontWeight="900"
        fill="#FFFFFF"
        letterSpacing="-1"
      >
        VI
      </text>
      {/* the "!" dot in VI's brand wordmark */}
      <circle cx="60" cy="46" r="4" fill="#FFC600" />
      <rect x="57" y="18" width="6" height="20" rx="1" fill="#FFFFFF" />

      {/* greyscale silhouette portrait sitting on the swoosh */}
      <defs>
        <clipPath id="viIdSilhouetteClip">
          <circle cx="105" cy="140" r="55" />
        </clipPath>
      </defs>
      <g clipPath="url(#viIdSilhouetteClip)">
        <rect x="50" y="85" width="110" height="110" fill="#E4E5EB" />
        {/* head */}
        <circle cx="105" cy="130" r="22" fill="#B7BAC4" />
        {/* shoulders */}
        <path d="M 60 210 Q 105 165 150 210 L 150 210 L 60 210 Z" fill="#B7BAC4" />
      </g>

      {/* yellow "hi" flourish overlapping the photo */}
      <text
        x="100" y="230"
        textAnchor="middle"
        fontFamily="Inter, sans-serif"
        fontSize="52"
        fontWeight="900"
        fill="#FFC600"
        letterSpacing="-2"
      >
        hi
      </text>

      {/* "I'm ____" caption at the bottom */}
      <text
        x="100" y="275"
        textAnchor="middle"
        fontFamily="Inter, sans-serif"
        fontSize="18"
        fontWeight="700"
        fill="#FFFFFF"
      >
        I&apos;m &#95;&#95;&#95;&#95;&#95;
      </text>
    </svg>
  );
}



export default function ProctorGate({ assignmentId, onReady }) {
  const [step, setStep] = useState("consent"); // consent | id | verifying | failed | waiting_override | ready
  const [err, setErr] = useState("");
  const [selfie, setSelfie] = useState(null);
  const [idCard, setIdCard] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const [overrideInfo, setOverrideInfo] = useState(null);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const pollRef = useRef(null);

  const startCamera = async () => {
    setErr("");
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErr("This browser does not support webcam access. Please use the latest Chrome, Edge, Firefox or Safari on a laptop/desktop.");
      return;
    }
    // Optional: pre-check permission state so we can be precise on the diagnostic
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const camPerm = await navigator.permissions.query({ name: "camera" });
        // eslint-disable-next-line no-console
        console.log("[ProctorGate] camera permission =", camPerm.state);
      }
    } catch (_) { /* not supported on Safari < 16 */ }
    // Race against a 15s timeout — Safari can hang silently when prior denials are cached.
    const TIMEOUT_MS = 15000;
    let timer;
    const timeoutP = new Promise((_, reject) => {
      timer = setTimeout(() => {
        const err = new Error("getUserMedia did not resolve in 15s");
        err.name = "TimeoutError";
        reject(err);
      }, TIMEOUT_MS);
    });
    try {
      const s = await Promise.race([
        navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 }, audio: true }),
        timeoutP,
      ]);
      clearTimeout(timer);
      setStream(s);
      setStep("id");  // Stream is attached to the <video> via the useEffect below once it mounts.
    } catch (e) {
      clearTimeout(timer);
      // eslint-disable-next-line no-console
      console.error("[ProctorGate] getUserMedia failed:", e?.name, e?.message);
      let msg;
      if (e?.name === "TimeoutError") {
        msg = "The browser did not respond to the camera permission prompt within 15 seconds. This often happens on Safari when permission was previously denied. Fix: Safari menu → Settings → Websites → Camera (and Microphone) → set this site to 'Ask' or 'Allow', then click Retry. If that doesn't work, try Chrome.";
      } else if (e?.name === "NotAllowedError" || e?.name === "SecurityError") {
        msg = "You blocked or dismissed the camera/microphone permission. Click the camera icon in your browser's address bar (or open browser settings), allow Camera and Microphone for this site, then click 'Retry' below.";
      } else if (e?.name === "NotFoundError" || e?.name === "OverconstrainedError") {
        msg = "The browser cannot see a webcam/microphone. On macOS: open System Settings → Privacy & Security → Camera and Microphone → enable your browser → quit and reopen the browser. Then click 'Retry'.";
      } else if (e?.name === "NotReadableError" || e?.name === "TrackStartError") {
        msg = "Your webcam appears to be in use by another app (Zoom / Teams / FaceTime). Please close it and click 'Retry'.";
      } else {
        msg = `Camera/microphone access failed (${e?.name || "unknown error"}: ${e?.message || "no details"}). Please reload the page and try again.`;
      }
      setErr(msg);
    }
  };

  // Attach the live stream once the <video> element actually renders (after step transitions to "id").
  useEffect(() => {
    if (stream && step === "id" && !selfie && videoRef.current) {
      try {
        videoRef.current.srcObject = stream;
        const p = videoRef.current.play();
        if (p && typeof p.catch === "function") p.catch(() => { /* autoplay blocked is non-fatal */ });
      } catch (e) { /* ignore */ }
    }
  }, [stream, step, selfie]);

  const [videoReady, setVideoReady] = useState(false);

  const capture = (target) => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current, c = canvasRef.current;
    if (!v.videoWidth || !v.videoHeight) {
      setErr("Camera is still warming up — please wait a moment and try again.");
      return;
    }
    const w = 480, h = Math.round((v.videoHeight / v.videoWidth) * 480);
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(v, 0, 0, w, h);
    const dataUrl = c.toDataURL("image/jpeg", 0.85);
    if (target === "selfie") { setSelfie(dataUrl); setErr(""); }
  };

  const onIdUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ok = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "application/pdf"].includes(f.type)
      || f.name.toLowerCase().endsWith(".pdf");
    if (!ok) {
      setErr("Please upload a JPEG, PNG, WEBP or PDF of your VI Employee ID.");
      return;
    }
    if (f.size > 12 * 1024 * 1024) {
      setErr("File too large (max 12 MB). Please use a smaller scan.");
      return;
    }
    setErr("");
    const r = new FileReader();
    r.onload = () => setIdCard(r.result);
    r.readAsDataURL(f);
  };

  // ---- ID capture via live camera (desktop-friendly alternative to upload) ----
  const [idCamOpen, setIdCamOpen] = useState(false);
  const [idCamStream, setIdCamStream] = useState(null);
  const [idCamErr, setIdCamErr] = useState("");
  const idVideoRef = useRef(null);

  const openIdCamera = async () => {
    setIdCamErr("");
    setIdCamOpen(true);
    try {
      // Prefer the rear camera (facingMode:'environment') on phones/tablets; on
      // laptops without a rear cam, the browser falls back to the built-in one.
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      setIdCamStream(s);
    } catch (e) {
      setIdCamErr(
        e?.name === "NotAllowedError"
          ? "Camera permission denied. Enable camera access for this site and try again, or use the Upload option."
          : e?.name === "NotFoundError"
            ? "No camera detected. Please use the Upload option instead."
            : `Camera could not start (${e?.name || "error"}). Please use the Upload option instead.`,
      );
    }
  };

  useEffect(() => {
    if (idCamOpen && idCamStream && idVideoRef.current) {
      try {
        idVideoRef.current.srcObject = idCamStream;
        const p = idVideoRef.current.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch (_) { /* ignore */ }
    }
  }, [idCamOpen, idCamStream]);

  const closeIdCamera = () => {
    if (idCamStream) idCamStream.getTracks().forEach((t) => t.stop());
    setIdCamStream(null);
    setIdCamOpen(false);
    setIdCamErr("");
  };

  const captureIdFromCamera = () => {
    if (!idVideoRef.current) return;
    const v = idVideoRef.current;
    const c = document.createElement("canvas");
    c.width = v.videoWidth || 1280;
    c.height = v.videoHeight || 960;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    // JPEG @0.9 keeps the payload under ~500 KB while preserving OCR-legible text.
    setIdCard(c.toDataURL("image/jpeg", 0.9));
    closeIdCamera();
  };

  const proceedToTest = async () => {
    try { await document.documentElement.requestFullscreen(); } catch (e) { /* fullscreen optional */ }
    setStep("ready");
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setTimeout(() => onReady(), 400);
  };

  const verify = async () => {
    setStep("verifying"); setErr("");
    try {
      // Client-side ceiling of 60s — Gemini can be slow on PDFs; backend
      // caps its own vision call at 45s so this is the safety net for the
      // network path between the browser and the ingress.
      const v = await api.post("/proctor/id-verify", {
        assignment_id: assignmentId,
        selfie_base64: selfie.split(",")[1],
        id_card_base64: idCard.split(",")[1],
      }, { timeout: 60000 });
      setVerdict(v.data);
      if (v.data.same_person && (v.data.confidence ?? 0.5) >= 0.55 && v.data.id_looks_genuine !== false) {
        await proceedToTest();
      } else {
        setStep("failed");
      }
    } catch (e) {
      // Distinguish a client-side timeout from a real API error so we can
      // present a clearer next step instead of a raw axios message.
      if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
        setErr("Verification is taking longer than expected. Please try again, or request manual review below.");
        setStep("failed");
        setVerdict({ same_person: false, confidence: 0, id_looks_genuine: true, fallback: true, timeout: true, reasoning: "Network timeout while verifying." });
      } else {
        setErr(formatApiError(e));
        setStep("id");
      }
    }
  };

  const requestOverride = async () => {
    setErr("");
    try {
      await api.post(`/proctor/request-override/${assignmentId}`);
      setStep("waiting_override");
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  // Poll for admin override decision while in waiting_override
  useEffect(() => {
    if (step !== "waiting_override") return undefined;
    const poll = async () => {
      try {
        const r = await api.get(`/proctor/id-status/${assignmentId}`);
        setOverrideInfo(r.data);
        if (r.data.override_granted) {
          clearInterval(pollRef.current);
          await proceedToTest();
        }
      } catch (e) { /* ignore */ }
    };
    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, assignmentId]);

  useEffect(() => () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    if (idCamStream) idCamStream.getTracks().forEach((t) => t.stop());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-3xl mx-auto fade-in" data-testid="proctor-gate">
      <canvas ref={canvasRef} className="hidden" />

      {step === "consent" && (
        <div className="card p-8">
          <div className="overline mb-2 !text-[#EE2737]">PROCTORED ASSESSMENT · DPDP CONSENT</div>
          <h1 className="font-heading font-black text-3xl mb-4">Before you begin</h1>
          <p className="text-[14px] text-[#0A0A0A] leading-relaxed mb-5">
            This assessment is <b>AI-proctored</b> to ensure a fair evaluation. By continuing, you consent to the following:
          </p>
          <ul className="space-y-2.5 text-[13.5px] text-[#525252] leading-relaxed mb-6">
            <li className="flex gap-3"><Camera size={16} className="text-[#EE2737] mt-0.5 flex-shrink-0" /> Your webcam captures still images every <b>15 seconds</b> while you take the test.</li>
            <li className="flex gap-3"><ShieldCheck size={16} className="text-[#EE2737] mt-0.5 flex-shrink-0" /> Your microphone is monitored for voices/sounds (only loudness, no recording is stored).</li>
            <li className="flex gap-3"><AlertTriangle size={16} className="text-[#EE2737] mt-0.5 flex-shrink-0" /> Tab switching, copy/paste, and exiting fullscreen are logged.</li>
            <li className="flex gap-3"><Maximize2 size={16} className="text-[#EE2737] mt-0.5 flex-shrink-0" /> The test runs in fullscreen. Every warning is flagged for the Integrity Center — you can continue the test, but repeated flags may affect your certification review.</li>
          </ul>
          <div className="bg-[#FEF3C7] border-l-4 border-[#D97706] p-3 text-[12px] text-[#92400E] leading-relaxed mb-6">
            <strong>Privacy.</strong> Only frames flagged as suspicious are retained for 30 days. All other frames are discarded immediately after analysis. Your ID document and selfie are stored encrypted and used only for one-time identity verification. The full evidence log is reviewable by Mercury Solutions and your Vodafone Idea L&D leadership.
          </div>
          {err && (
            <div className="bg-[#FEE2E2] border-2 border-[#DC2626] text-[#991B1B] px-4 py-3 mb-4" data-testid="proctor-cam-denied">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                <div className="text-sm leading-relaxed">{err}</div>
              </div>
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <button onClick={startCamera} className="btn btn-primary" data-testid="proctor-consent-grant">
              <Camera size={14} /> {err ? "Retry · grant camera & mic" : "I consent · grant camera & mic"}
            </button>
            {err && (
              <button onClick={() => window.location.reload()} className="btn btn-secondary" data-testid="proctor-reload">
                Reload page
              </button>
            )}
          </div>
        </div>
      )}

      {(step === "id" || step === "verifying" || step === "failed") && (
        <div className="card p-8">
          <div className="overline mb-2 !text-[#EE2737]">PROCTORED ASSESSMENT · IDENTITY VERIFICATION</div>
          <h1 className="font-heading font-black text-2xl mb-2">Verify your identity</h1>
          <p className="text-[13px] text-[#525252] leading-relaxed mb-6">
            Capture a clear selfie and upload a photo of your <strong>Vodafone Idea (VI) Employee ID card</strong>
            &nbsp;— it&apos;s the only ID accepted for this certification. The AI verifier compares your face against the
            photo on the card.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <div>
              <div className="overline mb-2">1. SELFIE (LIVE)</div>
              <div className="aspect-[4/3] bg-black mb-2 overflow-hidden">
                {selfie ? (
                  <img src={selfie} alt="selfie" className="w-full h-full object-cover" data-testid="selfie-preview" />
                ) : (
                  <video
                    ref={videoRef}
                    muted
                    playsInline
                    autoPlay
                    onLoadedMetadata={() => setVideoReady(true)}
                    className="w-full h-full object-cover"
                    data-testid="selfie-video"
                  />
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => capture("selfie")}
                  disabled={!!selfie || step !== "id" || !videoReady}
                  className="btn btn-secondary !text-xs flex-1"
                  data-testid="capture-selfie"
                >
                  <Camera size={13} /> {selfie ? "Captured" : (videoReady ? "Capture selfie" : "Warming up camera…")}
                </button>
                {selfie && step === "id" && (
                  <button onClick={() => { setSelfie(null); setVideoReady(false); }} className="btn btn-ghost !text-xs" data-testid="retake-selfie">Retake</button>
                )}
              </div>
            </div>

            <div>
              <div className="overline mb-2">2. VI EMPLOYEE ID</div>
              <div className="aspect-[4/3] bg-[#F8F9FA] mb-2 overflow-hidden border border-[#E5E7EB] flex items-center justify-center relative">
                {idCard ? (
                  idCard.startsWith("data:application/pdf") ? (
                    <div className="text-center p-4" data-testid="id-preview">
                      <div className="text-[42px] mb-2">📄</div>
                      <div className="text-[12px] font-bold">PDF uploaded</div>
                      <div className="text-[10px] text-[#878787] mt-1">First page will be sent to the verifier.</div>
                    </div>
                  ) : (
                    <img src={idCard} alt="ID" className="w-full h-full object-contain" data-testid="id-preview" />
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center gap-1.5" data-testid="id-sample">
                    <ViIdSample />
                    <div className="text-[10px] uppercase tracking-widest text-[#525252] font-bold">Sample · your card will look similar</div>
                    <div className="text-[10px] text-[#878787]">No photo yet</div>
                  </div>
                )}
              </div>
              <div className="flex gap-2 mb-1">
                <button
                  type="button"
                  onClick={openIdCamera}
                  disabled={step !== "id"}
                  className="btn btn-secondary !text-xs flex-1"
                  data-testid="id-camera-btn"
                  title="Use your camera to snap a live photo of the ID card"
                >
                  <Camera size={13} /> {idCard ? "Retake with camera" : "Take photo"}
                </button>
                <label
                  className={`btn btn-ghost !text-xs flex-1 cursor-pointer ${step !== "id" ? "opacity-50 pointer-events-none" : ""}`}
                  data-testid="id-upload-label"
                >
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.pdf"
                    onChange={onIdUpload}
                    disabled={step !== "id"}
                    className="hidden"
                    data-testid="id-upload"
                  />
                  {idCard ? "Choose another file" : "Upload file"}
                </label>
              </div>
              <div className="text-[10px] text-[#878787] mt-1">Tap <em>Take photo</em> to use your camera, or <em>Upload file</em> to pick from your gallery / files — JPEG/PNG/WEBP/HEIC/PDF · max 12&nbsp;MB</div>
            </div>
          </div>

          {idCamOpen && (
            <div
              className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
              data-testid="id-camera-modal"
            >
              <div className="bg-white max-w-lg w-full flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 bg-[#0A0A0A] text-white flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#EE2737] font-bold">CAPTURE VI EMPLOYEE ID</div>
                    <div className="font-heading font-bold text-sm">Hold the card flat inside the frame</div>
                  </div>
                  <button
                    onClick={closeIdCamera}
                    className="text-white/70 hover:text-white text-lg"
                    data-testid="id-camera-close"
                    aria-label="Close camera"
                  >
                    ×
                  </button>
                </div>
                <div className="p-3">
                  {idCamErr ? (
                    <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-xs px-3 py-3" data-testid="id-camera-err">
                      {idCamErr}
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-black overflow-hidden">
                      <video
                        ref={idVideoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-contain"
                        data-testid="id-camera-video"
                      />
                    </div>
                  )}
                  <div className="text-[10px] text-[#878787] mt-2 leading-relaxed">
                    Fill the frame with your VI Employee ID. Ensure the name and photo are clearly readable. Good, even lighting works best.
                  </div>
                </div>
                <div className="border-t border-[#E5E7EB] px-5 py-3 flex items-center justify-between gap-2">
                  <button
                    onClick={closeIdCamera}
                    className="btn btn-ghost !text-xs"
                    data-testid="id-camera-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={captureIdFromCamera}
                    disabled={!idCamStream || !!idCamErr}
                    className="btn btn-primary !text-xs"
                    data-testid="id-camera-capture"
                  >
                    <Camera size={13} /> Capture photo
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === "failed" && verdict && (
            <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-4 py-3 mb-4 leading-relaxed" data-testid="verify-failed">
              <strong>AI verification did not match.</strong> {verdict.reasoning}
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={() => { setSelfie(null); setIdCard(null); setVerdict(null); setStep("id"); }} className="btn btn-ghost !text-xs" data-testid="retry-verify">Retake selfie / ID</button>
                <button onClick={requestOverride} className="btn btn-primary !text-xs" data-testid="request-override">
                  <ShieldCheck size={12} /> Request manual review by Mercury
                </button>
              </div>
              <div className="text-[11px] text-[#525252] mt-2">If you believe the photo on file is wrong, ask a Mercury admin to approve a one-time override. You&apos;ll be admitted as soon as they approve.</div>
            </div>
          )}
          {err && <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-3 py-2 mb-4">{err}</div>}

          <button
            onClick={verify}
            disabled={!selfie || !idCard || step === "verifying"}
            className="btn btn-primary"
            data-testid="run-verify"
          >
            <ShieldCheck size={14} /> {step === "verifying" ? "Verifying…" : "Verify & enter assessment"}
          </button>
        </div>
      )}

      {step === "waiting_override" && (
        <div className="card p-10 text-center" data-testid="waiting-override">
          <Hourglass size={36} className="text-[#D97706] mx-auto mb-3 animate-pulse" />
          <h2 className="font-heading font-black text-2xl mb-2">Waiting for Mercury admin approval</h2>
          <p className="text-[13px] text-[#525252] leading-relaxed max-w-md mx-auto mb-4">
            We've notified the Mercury Solutions admin team. As soon as your identity is manually approved, this page will automatically unlock and admit you to the assessment.
          </p>
          <p className="text-[11px] text-[#878787]">
            Override status: {overrideInfo?.override_granted ? "GRANTED" : overrideInfo?.override_requested ? "PENDING REVIEW" : "REQUESTING…"}
          </p>
          <p className="text-[11px] text-[#878787] mt-1">You can keep this tab open · we're polling every 5 seconds.</p>
        </div>
      )}

      {step === "ready" && (
        <div className="card p-10 text-center fade-in">
          <ShieldCheck size={36} className="text-[#10B981] mx-auto mb-3" />
          <h2 className="font-heading font-black text-2xl mb-2">Verified · Starting assessment</h2>
          <p className="text-[13px] text-[#525252]">Entering fullscreen. Good luck!</p>
        </div>
      )}
    </div>
  );
}
